/**
 * Lucide icons, embedded instead of loaded.
 *
 * Only the paths of the symbols actually used are here — no dependency, no
 * CDN, no package at runtime. This is the same decision as for the font and
 * the dictionary: the app must run offline, and a symbol that comes from the
 * network is missing exactly when Elmar writes.
 *
 * All paths are drawn for a 24x24 box and use `currentColor` — thus the
 * symbols automatically take the color of the text they are in, also in the
 * dark appearance.
 *
 * Taken from `lucide-static` (ISC license), https://lucide.dev
 */

const PATHS = {
  'x':
    '<path d="M18 6 6 18" /> <path d="m6 6 12 12" />',
  'chevron-down':
    '<path d="m6 9 6 6 6-6" />',
  'printer':
    '<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /> <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" /> <rect x="6" y="14" width="12" height="8" rx="1" />',
  // Lucide has no comma of its own; the pilcrow is the mark for punctuation.
  'pilcrow':
    '<path d="M13 4v16" /> <path d="M17 4v16" /> <path d="M19 4H9.5a4.5 4.5 0 0 0 0 9H13" />',
  'menu':
    '<path d="M4 6h16" /> <path d="M4 12h16" /> <path d="M4 18h16" />',
  'camera':
    '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /> <circle cx="12" cy="13" r="3" />',
  'spell-check':
    '<path d="m20 15-5.5 5.5L12 18" /> <path d="m4 16 6-12 5.115 10.23" /> <path d="M6 12h8" />',
  'file-text':
    '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /> <path d="M14 2v5a1 1 0 0 0 1 1h5" /> <path d="M10 9H8" /> <path d="M16 13H8" /> <path d="M16 17H8" />',
  'file-plus':
    '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /> <path d="M14 2v5a1 1 0 0 0 1 1h5" /> <path d="M9 15h6" /> <path d="M12 18v-6" />',
  'folder-open':
    '<path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />',
  'save':
    '<path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /> <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" /> <path d="M7 3v4a1 1 0 0 0 1 1h7" />',
  'circle-check':
    '<circle cx="12" cy="12" r="10" /> <path d="m16 9-5.5 5.5L8 12" />',
  'sparkles':
    '<path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" /> <path d="M20 2v4" /> <path d="M22 4h-4" /> <circle cx="4" cy="20" r="2" />',
  'arrow-right':
    '<path d="M5 12h14" /> <path d="m12 5 7 7-7 7" />',
  'trash-2':
    '<path d="M10 11v6" /> <path d="M14 11v6" /> <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /> <path d="M3 6h18" /> <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />',
  'download':
    '<path d="M12 15V3" /> <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /> <path d="m7 10 5 5 5-5" />',
  'type':
    '<path d="M12 4v16" /> <path d="M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2" /> <path d="M9 20h6" />',
  'align-justify':
    '<path d="M3 5h18" /> <path d="M3 12h18" /> <path d="M3 19h18" />',
  'file':
    '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /> <path d="M14 2v5a1 1 0 0 0 1 1h5" />',
  'layout-grid':
    '<rect width="7" height="7" x="3" y="3" rx="1" /> <rect width="7" height="7" x="14" y="3" rx="1" /> <rect width="7" height="7" x="14" y="14" rx="1" /> <rect width="7" height="7" x="3" y="14" rx="1" />',
  'check':
    '<path d="M20 6 9 17l-5-5" />',
  'pencil':
    '<path d="M12 19h8" /> <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />',
  'chevron-right':
    '<path d="m9 18 6-6-6-6" />',
  'chevron-left':
    '<path d="m15 18-6-6 6-6" />',
  // The five kinds of exercise — see `Kind` in Exercise.tsx.
  'search':
    '<path d="m21 21-4.34-4.34" /> <circle cx="11" cy="11" r="8" />',
  'case-sensitive':
    '<path d="m3 15 4-8 4 8" /> <path d="M4 13h6" /> <circle cx="18" cy="12" r="3" /> <path d="M21 9v6" />',
  'scale':
    '<path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" /> <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" /> <path d="M7 21h10" /> <path d="M12 3v18" /> <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />',
  // The strategy — an idea, a way to the answer.
  'lightbulb':
    '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /> <path d="M9 18h6" /> <path d="M10 22h4" />',
  // The word that does not exist.
  'ban':
    '<circle cx="12" cy="12" r="10" /> <path d="m4.9 4.9 14.2 14.2" />',
  'puzzle':
    '<path d="M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z" />',
}

/** The names that exist. A typo is thereby a compilation error. */
export type IconName = keyof typeof PATHS

/**
 * Ein Symbol als Komponente.
 *
 * Purely decorative: `aria-hidden` keeps it out of the read-aloud order.
 * Where a symbol stands alone on a button, the button carries the label
 * through `aria-label` — otherwise it would be silent for a screen reader.
 *
 * The paths still stand as markup in PATHS, instead of as JSX: they come
 * unchanged from lucide-static, and that way the comparison with the source
 * stays simple. `dangerouslySetInnerHTML` is harmless here — the string stands
 * in this file and comes from nowhere outside.
 */
export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  return (
    <svg
      class={`icon icon-${name}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
      dangerouslySetInnerHTML={{ __html: PATHS[name] }}
    />
  )
}

/**
 * The logo — the same scribble as the favicon.
 *
 * One single line winding across the whole square: a scribble, as the name
 * says. It stands in `public/favicon.svg` and here, and that doubling is
 * deliberate — the browser tab needs a FILE it can fetch before anything is
 * rendered, the app needs a SHAPE that takes the colour around it.
 *
 * `fill="currentColor"` does that: muted in the bar, bright on the sign-in
 * screen, without a second copy per place.
 *
 * The `<style>` block from the file stays out. It sets the colour by
 * `prefers-color-scheme`, which is right for a favicon standing alone in a
 * tab and would override the surrounding colour here.
 */
export function Logo({ size = 24, title }: { size?: number; title?: string }) {
  return (
    <svg
      class="logo-mark"
      width={size}
      height={size}
      viewBox="0 0 96 96"
      fill="none"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : 'true'}
      aria-label={title}
      focusable="false"
    >
      <path fill="currentColor" d="M94.8 78.8c1.8-1.5-.1-4.8-2.3-4-7 2.5-13.8 5.2-20.7 7.9 7.4-5.3 14.8-10.4 22.6-15 2.3-1.3.8-5.4-1.9-4.4-12.5 4.6-24.7 9.8-36.8 15.1a806 806 0 0 1 38.6-24.2c2.7-1.6.3-5.5-2.4-4.1C72.5 59.3 53 68.3 33.3 76.9c20.3-12.3 41.3-23.8 61-37 2.6-1.7.4-5.5-2.4-4.1-14.9 7-30 13.6-45.1 20.3 16.3-9.9 32.1-20.5 47.5-31.8 2.4-1.8.1-5.9-2.4-4.1-12.5 8.7-27.5 13.5-41.2 20.3-8 4-16.1 8.2-24.4 11.8 7.9-4 15.9-7.9 23.4-12.6 14.9-9.3 29.1-19.6 44.6-27.9 2.7-1.5.3-5.5-2.4-4.1C72 17.6 51.7 26.9 31.3 35.9c4.2-2.6 8.5-5.2 12.7-7.9 1.9-1.2 3.8-2.4 5.7-3.7A535 535 0 0 0 81.5 7.2c2.4-1.4.8-5-1.9-4.4C68 5.5 57.9 13.2 48 19.8c-9.1 4.5-18.5 8.5-28 11.9C33.4 24 47.2 17.3 58.9 7.1c2.1-1.9-.6-4.9-2.9-3.8C44.7 9 33 13.6 21.2 18.1q8.4-5.55 17.1-10.5c2.7-1.6.3-5.4-2.4-4.1-6.8 3.1-13.9 5.4-21 7.7 1.7-1.5 3.4-2.9 5.2-4.3 2-1.6-.2-4.6-2.3-4-4.6 1.2-9.4 1.4-14 2.1C.7 5.3 2 10 5 9.5a46 46 0 0 1 5.2-.6c-2.7 2.3-5.4 4.7-8.2 6.9-1.9 1.5.1 4.8 2.3 4 3.9-1.4 7.8-2.7 11.7-4-4.6 3.1-9 6.3-13.4 9.7-1.8 1.5.2 4.9 2.3 4 10-4 20.1-7.8 30-11.8-11 6.4-22.6 12-33 19.5-1.9 1.6-.7 5.2 2.1 4.5 6-1.6 12-3.4 17.8-5.6-6.4 4-12.8 8.1-19.1 12.3-2.6 1.7-.4 5.4 2.4 4.1C22 45.2 38.9 37.8 55.7 30.2c-2.8 1.8-5.6 3.6-8.5 5.4C32.4 44.8 16 51 1.6 60.6c-2.3 1.5-.9 5 1.9 4.4 16.6-3.5 32-11.6 47-19.1 4.1-2 8.4-3.9 12.6-5.8-19.6 12.9-40 24.6-61 35.1-2.5 1.2-.7 5 1.9 4.4 11.4-2.8 22.6-9.1 33.3-13.9 7.6-3.5 15.3-6.9 23-10.3C42 66.2 23.5 76.6 6 88.7c-2.6 1.8-.4 5.3 2.4 4.1C25.1 85.8 41.6 78.5 58 71c-8.9 5.8-17.7 11.7-26.3 17.8-2.6 1.8-.4 5.4 2.4 4.1 12.6-5.5 25.2-11.2 38-16.5-5.7 4-11.2 8.1-16.8 12.2-2 1.5-.9 5.3 1.9 4.4 8.9-3 17.6-6.5 26.3-9.8-1.6 1.8-3.2 3.6-4.9 5.3s0 4.6 2.3 4c4.3-1 8.5-2.6 12.9-3.6 3-.7 1.7-5.3-1.3-4.6-1.6.4-3.1.8-4.6 1.3 2.1-2.4 4.3-4.7 6.9-6.8" />
    </svg>
  )
}
